import { ref, reactive, type Ref } from "vue";
import { generateApolloClient } from "@/common/apolloClientUtil";
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
} from "@/common/localStorageUtil";
import {
  ListDeliveryAvailabilityDocument,
  type ListDeliveryAvailabilityQuery,
  GetDeliveryAreaDocument,
  type GetDeliveryAreaQuery,
  type GetDeliveryAreaQueryVariables,
  ListDeliveryAreaDocument,
  type ListDeliveryAreaQuery,
  type ListDeliveryAreaQueryVariables,
  type TransPickupRequestUpdateInput,
} from "@/generated/graphql";
import type { ApolloQueryResult } from "@apollo/client/core";
import type {
  DeliveryAvailabilityData,
  DeliveryArea,
  DeliveryStartMinit,
  BeforData,
  DeliveryAreaSelectItem,
} from "../types";
import { WEEKS } from "../constants";

/**
 * 画面で共有する状態を初期化して返却する composable。
 *
 * @returns 画面で利用するリアクティブな状態オブジェクト
 */
export const useDeliveryMoveDeliveryTimeState = () => {
  // 配達エリア名
  const listDeliveryArea = ref<
    (DeliveryAreaSelectItem | null | undefined)[] | null | undefined
  >(null);
  const deliveryAreaName = ref("");
  const acceptableEndTime = ref("");

  // 配達開始時間の選択ボックス
  const deliveryStartHourItem = ref<number[]>([]);
  const deliveryStartHourValues = ref<number | null>(null);
  const deliveryStartMinitItem = ref<DeliveryStartMinit[]>([
    { label: "00", deliveryStartMinit: 0 },
    { label: "30", deliveryStartMinit: 1 },
  ]);
  const deliveryStartMinitValues = ref<DeliveryStartMinit | null | undefined>(undefined);

  // 配達希望時間
  const befor_data = reactive<BeforData>({
    delivery_date: "",
    delivery_id: 999,
    irregularDeliveryStartTime: [],
  });

  const irregularDeliveryStartTime = ref("");
  const irregularDeliveryEndTime = ref("");
  const deliveryId_0_Flg = ref(false);
  const deliveryStartTime = ref("");
  const deliveryEndTime = ref("");
  const overDeliveryEndTime = ref(false);

  // ２件分配達
  const doubleDeliOriconCount = ref<number | null>(null);
  const isDoubleDeli = ref(false);
  const pre_double_deli_oricon_flg = ref<number | null>(0);

  // チャーター便
  const charterDeliOriconCount = ref<number | null>(null);
  const isCharterDeli = ref(false);
  const pre_charter_deli_oricon_flg = ref<number | null>(0);

  // 配達便情報のヘッダー
  const deliveryAvailabilityHeader = ref<Map<string, string>>(new Map());
  const deliveryAvailabilityData = ref<DeliveryAvailabilityData[][]>([]);
  const disp_deliveryAvailabilityData = ref<DeliveryAvailabilityData[][]>([]);
  const display_delivery_time = ref("");

  // 便選択色変更
  const deliverySelectDayBox = ref<string>("");
  const deliverySelectBox = ref<string>("");

  return {
    listDeliveryArea,
    deliveryAreaName,
    acceptableEndTime,
    deliveryStartHourItem,
    deliveryStartHourValues,
    deliveryStartMinitItem,
    deliveryStartMinitValues,
    befor_data,
    irregularDeliveryStartTime,
    irregularDeliveryEndTime,
    deliveryId_0_Flg,
    deliveryStartTime,
    deliveryEndTime,
    overDeliveryEndTime,
    doubleDeliOriconCount,
    isDoubleDeli,
    pre_double_deli_oricon_flg,
    charterDeliOriconCount,
    isCharterDeli,
    pre_charter_deli_oricon_flg,
    deliveryAvailabilityHeader,
    deliveryAvailabilityData,
    disp_deliveryAvailabilityData,
    display_delivery_time,
    deliverySelectDayBox,
    deliverySelectBox,
  };
};

interface UseDeliveryMoveDeliveryTimeParams {
  listDeliveryArea: Ref<(DeliveryAreaSelectItem | null | undefined)[] | null | undefined>;
  deliveryAreaName: Ref<string>;
  acceptableEndTime: Ref<string>;
  deliveryStartHourItem: Ref<number[]>;
  deliveryStartHourValues: Ref<number | null>;
  deliveryStartMinitItem: Ref<DeliveryStartMinit[]>;
  deliveryStartMinitValues: Ref<DeliveryStartMinit | null | undefined>;
  befor_data: BeforData;
  irregularDeliveryStartTime: Ref<string>;
  irregularDeliveryEndTime: Ref<string>;
  deliveryId_0_Flg: Ref<boolean>;
  deliveryStartTime: Ref<string>;
  deliveryEndTime: Ref<string>;
  overDeliveryEndTime: Ref<boolean>;
  deliveryAvailabilityHeader: Ref<Map<string, string>>;
  deliveryAvailabilityData: Ref<DeliveryAvailabilityData[][]>;
  disp_deliveryAvailabilityData: Ref<DeliveryAvailabilityData[][]>;
  display_delivery_time: Ref<string>;
  deliverySelectDayBox: Ref<string>;
  deliverySelectBox: Ref<string>;
  editDeliveryRequest: Ref<TransPickupRequestUpdateInput | null>;
  shopSelectValues_from: Ref<{ shop_code: string; name: string }>;
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
}

/**
 * 配達時間の生成・整形ロジックを提供する composable。
 *
 * @returns 画面ロジックで利用する状態・関数
 */
export const useDeliveryMoveDeliveryTime = ({
  listDeliveryArea,
  deliveryAreaName,
  acceptableEndTime,
  deliveryStartHourItem,
  deliveryStartHourValues,
  deliveryStartMinitItem,
  deliveryStartMinitValues,
  befor_data,
  irregularDeliveryStartTime,
  irregularDeliveryEndTime,
  deliveryId_0_Flg,
  deliveryStartTime,
  deliveryEndTime,
  overDeliveryEndTime,
  deliveryAvailabilityHeader,
  deliveryAvailabilityData,
  disp_deliveryAvailabilityData,
  display_delivery_time,
  deliverySelectDayBox,
  deliverySelectBox,
  editDeliveryRequest,
  shopSelectValues_from,
  errdialog,
  errMessage,
}: UseDeliveryMoveDeliveryTimeParams) => {
  /**
   * エリアボタン選択時 @click
   */
  const onClickListDeliveryArea = async (item: DeliveryArea): Promise<void> => {
    deliveryAvailabilityHeader.value = new Map();
    disp_deliveryAvailabilityData.value = [];
    let addAry: DeliveryAvailabilityData[] = [];

    editDeliveryRequest.value!.area_code = item.area_code;
    deliveryAreaName.value = item.area_name;
    acceptableEndTime.value = item.acceptable_end_time;

    deliveryAvailabilityData.value.forEach((listVal) => {
      addAry = [];
      listVal.forEach((value) => {
        if (String(value.area_code) === String(item.area_code)) {
          addAry.push(value);
          // 配達便の列
          if (value.delivery_id_desc !== "") {
            deliveryAvailabilityHeader.value.set(value.delivery_id_desc, value.delivery_id_desc);
          }
          // 都度便判断
          if (listVal[0].delivery_id === 0) {
            deliveryId_0_Flg.value = true;
            deliveryStartTime.value = listVal[0].delivery_start_time;
          } else {
            deliveryId_0_Flg.value = false;
            deliveryStartTime.value = "";
          }
        }
      });
      if (addAry.length > 0) {
        disp_deliveryAvailabilityData.value.push(addAry);
      }
    });
  };

  /**
   * エリア一覧情報を取得する
   */
  const getAreaList = async (address: string, area_code?: string): Promise<void> => {
    // クリア
    deliveryAreaName.value = "";
    deliveryAvailabilityHeader.value = new Map();
    deliveryAvailabilityData.value = [];
    disp_deliveryAvailabilityData.value = [];
    deliveryId_0_Flg.value = false;
    deliveryStartHourItem.value = [];
    deliveryStartHourValues.value = undefined;
    deliveryStartMinitValues.value = undefined;
    display_delivery_time.value = "";
    editDeliveryRequest.value!.area_code = area_code ?? "";

    // graphql接続
    const apolloClient = await generateApolloClient();

    // 住所から配達エリア一覧を取得
    try {
      const listArea = await apolloClient.query<ListDeliveryAreaQuery, ListDeliveryAreaQueryVariables>({
        query: ListDeliveryAreaDocument,
        variables: {
          chain_code: localStorageGetChainCode(),
          customer_code: localStorageGetCustomerCode(),
          shop_code: shopSelectValues_from.value.shop_code,
          address,
        },
      });
      // GraphQL結果は読み取り専用のため、ディープコピーを作成
      listDeliveryArea.value = listArea.data.listDeliveryArea
        ? JSON.parse(JSON.stringify(listArea.data.listDeliveryArea))
        : null;
      await getAreaInfo();
      if (listDeliveryArea.value?.length === 1) {
        editDeliveryRequest.value!.area_code = listDeliveryArea.value[0]!.area_code;
        await onClickListDeliveryArea(listDeliveryArea.value[0]!);
      }
    } catch (err: unknown) {
      if (err instanceof Error) err.message = "配達エリア一覧の取得に失敗しました。";
      else throw new Error("配達エリア一覧の取得に失敗しました。（Unknown error）");
      throw err;
    }
  };

  /**
   * エリア店舗情報取得
   */
  const getAreaInfo = async (): Promise<void> => {
    try {
      const apolloClient = await generateApolloClient();
      let acceptableEndTimeAry: string[] = [];

      //住所から配達エリアを取得
      for (let i = 0; i < listDeliveryArea.value!.length; i++) {
        const area_data = await apolloClient.query<GetDeliveryAreaQuery, GetDeliveryAreaQueryVariables>({
          query: GetDeliveryAreaDocument,
          variables: {
            chain_code: localStorageGetChainCode(),
            customer_code: localStorageGetCustomerCode(),
            shop_code: shopSelectValues_from.value.shop_code,
            area_code: listDeliveryArea.value![i]!.area_code,
          },
          fetchPolicy: "no-cache",
        });

        // getDeliveryAreaとacceptable_end_timeのnullチェック
        const deliveryArea = area_data.data.getDeliveryArea;
        if (deliveryArea && deliveryArea.acceptable_end_time) {
          acceptableEndTimeAry = deliveryArea.acceptable_end_time.split(":");
          if (acceptableEndTimeAry.length >= 2 && listDeliveryArea.value![i]) {
            listDeliveryArea.value![i]!.acceptable_end_time =
              acceptableEndTimeAry[0].padStart(2, "0") + ":" + acceptableEndTimeAry[1].padStart(2, "0");
          }
          if (
            listDeliveryArea.value![i]!.area_code === editDeliveryRequest.value!.area_code
          ) {
            deliveryAreaName.value = deliveryArea.area_name;
            acceptableEndTime.value = deliveryArea.acceptable_end_time;
          }
        }
      }
    } catch (err: unknown) {
      if (err instanceof Error) err.message = "配達エリアの取得に失敗しました。";
      else throw new Error("配達エリアの取得に失敗しました。（Unknown error）");
      throw err;
    }
    // 配達時間一覧取得する
    await getDeliveryTimeList();
  };

  /**
   * 配達時間一覧取得する
   */
  const getDeliveryTimeList = async (): Promise<void> => {
    try {
      // graphql接続
      const apolloClient = await generateApolloClient();

      let result: ApolloQueryResult<ListDeliveryAvailabilityQuery>;
      let list: (DeliveryAvailabilityData | null)[] = [];

      for (let i = 0; i < listDeliveryArea.value!.length; i++) {
        // 配達時間一覧取得
        result = await apolloClient.query<ListDeliveryAvailabilityQuery>({
          query: ListDeliveryAvailabilityDocument,
          variables: {
            customer_code: localStorageGetCustomerCode(),
            chain_code: localStorageGetChainCode(),
            shop_code: shopSelectValues_from.value.shop_code,
            area_code: listDeliveryArea.value![i]!.area_code,
          },
          fetchPolicy: "no-cache",
        });

        list = result.data.listDeliveryAvailability ?? [];

        if (list.length <= 0) {
          errdialog.value = true;
          errMessage.value = "選択した住所で配達可能な時間帯はありません。";
          // クリア
          editDeliveryRequest.value!.area_code = "";
          deliveryAreaName.value = "";
          deliveryAvailabilityHeader.value = new Map();
          deliveryAvailabilityData.value = [];
          disp_deliveryAvailabilityData.value = [];
          return;
        }

        // 選択されているエリア
        let defaultAreaCode = "";
        let currentDate = "";
        let currentAvailability: DeliveryAvailabilityData[] = [];
        let idx = 0;
        let addDeliveryIdAry: number[] = [];
        let addCnt = 0;
        let current_area_code = "";

        // 全便ID格納
        addDeliveryIdAry = Array.from(
          new Set(
            list.filter((item) => item != null).map((item) => item!.delivery_id)
          )
        );

        list.forEach((value) => {
          if (value != null) {
            if (!defaultAreaCode) {
              defaultAreaCode = value.area_code;
            }
            if (value.area_code !== defaultAreaCode) {
              return;
            }
            current_area_code = listDeliveryArea.value![i]!.area_code;

            // 日付をYYYY-MM-DDに変更
            value.delivery_date = value.delivery_date.substring(0, 10);
            if (!currentDate || currentDate !== value.delivery_date) {
              currentDate = value.delivery_date;
              if (currentAvailability.length > 0) {
                // 他の日付の便IDを空データで追加
                if (addCnt < addDeliveryIdAry.length) {
                  for (let j = addCnt; j < addDeliveryIdAry.length; j++) {
                    // 配達日ごとの配達数の列
                    currentAvailability.push({
                      key: ++idx,
                      area_code: current_area_code,
                      delivery_id: addDeliveryIdAry[j],
                      delivery_id_desc: "",
                      delivery_date: value.delivery_date,
                      delivery_start_time: "",
                      delivery_end_time: "",
                      availability: "×",
                      already_requested: 0,
                    });
                  }
                }
                deliveryAvailabilityData.value.push(currentAvailability);
                if (current_area_code === editDeliveryRequest.value!.area_code) {
                  disp_deliveryAvailabilityData.value.push(currentAvailability);
                }
              }
              currentAvailability = [];
              idx = 0;
              addCnt = 0;
            }

            // 他の日付の便IDを空データで追加
            for (let j = addCnt; j < addDeliveryIdAry.length; j++) {
              if (addDeliveryIdAry[j] !== value.delivery_id) {
                // 配達日ごとの配達数の列
                currentAvailability.push({
                  key: ++idx,
                  area_code: current_area_code,
                  delivery_id: addDeliveryIdAry[j],
                  delivery_id_desc: "",
                  delivery_date: value.delivery_date,
                  delivery_start_time: "",
                  delivery_end_time: "",
                  availability: "×",
                  already_requested: 0,
                });
              } else {
                addCnt = j;
                break;
              }
            }

            // 配達日ごとの配達数の列
            currentAvailability.push({
              key: ++idx,
              area_code: current_area_code,
              delivery_id: value.delivery_id,
              delivery_id_desc: value.delivery_id_desc,
              delivery_date: value.delivery_date,
              delivery_start_time: value.delivery_start_time,
              delivery_end_time: value.delivery_end_time,
              availability: value.availability.trim(),
              already_requested: value.already_requested ?? 0,
            });
            addCnt++;
          }
        });

        if (currentAvailability.length > 0) {
          // 他の日付にある便IDを空データで追加
          if (addCnt < addDeliveryIdAry.length) {
            for (let j = addCnt; j < addDeliveryIdAry.length; j++) {
              // 配達日ごとの配達数の列
              currentAvailability.push({
                key: ++idx,
                area_code: current_area_code,
                delivery_id: addDeliveryIdAry[j],
                delivery_id_desc: "",
                delivery_date: currentDate,
                delivery_start_time: "",
                delivery_end_time: "",
                availability: "×",
                already_requested: 0,
              });
            }
          }
          deliveryAvailabilityData.value.push(currentAvailability);
          if (current_area_code === editDeliveryRequest.value!.area_code) {
            disp_deliveryAvailabilityData.value.push(currentAvailability);
          }
        }
      }

      // エリア一覧が1件の場合
      if (listDeliveryArea.value!.length === 1) {
        const first = deliveryAvailabilityData.value[0][0];
        if (first.delivery_id === 0) {
          deliveryId_0_Flg.value = true;
          deliveryStartTime.value = first.delivery_start_time;
        }
      }
    } catch (err: unknown) {
      if (err instanceof Error) {
        console.error(err);
      } else {
        console.error("未知のエラー:", err);
      }

      errdialog.value = true;
      errMessage.value = "選択した住所は配達エリア外です。";

      // クリア処理
      editDeliveryRequest.value!.area_code = "";
      deliveryAreaName.value = "";
      deliveryAvailabilityHeader.value = new Map();
      deliveryAvailabilityData.value = [];

      return;
    }
  };

  /**
   * 配達希望時間（過去日時チェック） @change
   */
  const chkDeliveryStartTime_Now = async (): Promise<boolean> => {
    const now = new Date();
    const nowDate =
      `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

    if (
      editDeliveryRequest.value!.required_delivery_date === nowDate &&
      deliveryStartHourValues.value !== null &&
      deliveryStartHourValues.value !== undefined &&
      (deliveryStartHourValues.value < now.getHours() ||
        (deliveryStartHourValues.value === now.getHours() &&
          deliveryStartMinitValues.value !== null &&
          deliveryStartMinitValues.value !== undefined &&
          Number(deliveryStartMinitValues.value.label) < now.getMinutes()))
    ) {
      return false;
    }
    return true;
  };

  /**
   * 配達希望時間変更イベント @change
   */
  const chkDeliveryStartTime = async (): Promise<boolean> => {
    const deliveryStartTimeAry = deliveryStartTime.value.split(":");
    if (
      (deliveryStartHourValues.value !== null &&
        deliveryStartHourValues.value !== undefined &&
        deliveryStartHourValues.value < Number(deliveryStartTimeAry[0])) ||
      (deliveryStartHourValues.value === Number(deliveryStartTimeAry[0]) &&
        deliveryStartMinitValues.value !== null &&
        deliveryStartMinitValues.value !== undefined &&
        Number(deliveryStartMinitValues.value.label) <
          Number(deliveryStartTimeAry[1]))
    ) {
      return false;
    } else {
      // 配達終了時間セット
      const endTime = deliveryStartHourValues.value! + 3;
      const deliveryEndTimeAry = deliveryEndTime.value.split(":");
      if (
        endTime > Number(deliveryEndTimeAry[0]) ||
        (endTime === Number(deliveryEndTimeAry[0]) &&
          deliveryStartMinitValues.value!.label !== deliveryEndTimeAry[1])
      ) {
        irregularDeliveryEndTime.value = deliveryEndTime.value;
      } else {
        irregularDeliveryEndTime.value =
          `${String(endTime).padStart(2, "0")}:${deliveryStartMinitValues.value!.label}:00`;
      }
      return true;
    }
  };

  /**
   * 配達希望時間（配達終了時刻超過チェック）
   */
  const chkDeliveryEndTimeOver = async (): Promise<boolean> => {
    const deliveryEndTimeAry = deliveryEndTime.value.split(":");
    if (
      deliveryStartHourValues.value! > Number(deliveryEndTimeAry[0]) ||
      (deliveryStartHourValues.value === Number(deliveryEndTimeAry[0]) &&
        Number(deliveryStartMinitValues.value!.label) > Number(deliveryEndTimeAry[1]))
    ) {
      return false;
    } else {
      return true;
    }
  };

  /**
   * 配達開始時間セット
   */
  const setDeliveryStartTime = async (): Promise<void> => {
    const now = new Date();
    const deliveryStartTimeAry = deliveryStartTime.value.split(":");
    if (
      deliveryStartHourValues.value !== undefined &&
      deliveryStartHourValues.value !== null &&
      deliveryStartMinitValues.value !== undefined &&
      deliveryStartMinitValues.value !== null
    ) {
      irregularDeliveryStartTime.value =
        String(deliveryStartHourValues.value).padStart(2, "0") +
        ":" +
        deliveryStartMinitValues.value.label;
    } else {
      let hour = 0;
      let minut = 0;
      if (
        now.getHours() < Number(deliveryStartTimeAry[0]) ||
        (now.getHours() === Number(deliveryStartTimeAry[0]) &&
          now.getMinutes() < Number(deliveryStartTimeAry[1]))
      ) {
        hour = Number(deliveryStartTimeAry[0]);
        minut = Number(deliveryStartTimeAry[1]);
      } else {
        if (now.getMinutes() < 30) {
          hour = now.getHours();
          minut = 30;
        } else {
          hour = now.getHours() + 1;
          minut = 0;
        }
      }
      irregularDeliveryStartTime.value =
        String(hour).padStart(2, "0") + ":" + String(minut).padStart(2, "0");
    }
  };

  /**
   * 配達終了時間セット
   */
  const setDeliveryEndTime = async (): Promise<void> => {
    const deliveryStartTimeAry = irregularDeliveryStartTime.value.split(":");
    const deliveryEndTimeAry = deliveryEndTime.value.split(":");
    const endTime = Number(deliveryStartTimeAry[0]) + 3;
    if (
      endTime > Number(deliveryEndTimeAry[0]) ||
      (endTime === Number(deliveryEndTimeAry[0]) &&
        deliveryStartTimeAry[1] !== deliveryEndTimeAry[1])
    ) {
      irregularDeliveryEndTime.value = deliveryEndTime.value;
    } else {
      irregularDeliveryEndTime.value =
        String(endTime).padStart(2, "0") + ":" + deliveryStartTimeAry[1];
    }
  };

  /**
   * 配達希望時間（時）変更イベント @change
   */
  const onChangeDeliveryStartHour = async (): Promise<void> => {
    if (deliveryStartHourValues.value !== undefined && deliveryStartHourValues.value !== null) {
      // 過去日時チェック
      if (!(await chkDeliveryStartTime_Now())) {
        deliveryStartHourValues.value = undefined;
        errdialog.value = true;
        errMessage.value = "配達希望時間は、現在以降の時間を入力してください。";
        return;
      }
      // 配達開始時間前チェック
      if (!(await chkDeliveryStartTime())) {
        deliveryStartHourValues.value = undefined;
        errdialog.value = true;
        errMessage.value =
          "配達希望時間は、選択した便の配達開始時間以降の時間を入力してください。";
        return;
      }
      // 受付終了時間超過チェック
      if (!(await chkDeliveryEndTimeOver())) {
        deliveryStartHourValues.value = undefined;
        errdialog.value = true;
        errMessage.value =
          "配達希望時間は、受付終了時間以前の時間を入力してください。";
        return;
      }
    }

    const now = new Date();
    const deliveryStartTimeAry = deliveryStartTime.value.split(":");
    const endTimeAry = acceptableEndTime.value.split(":");
    // 分セット
    deliveryStartMinitItem.value = [
      { label: "00", deliveryStartMinit: 0 },
      { label: "30", deliveryStartMinit: 1 },
    ];

    if (
      deliveryStartHourValues.value !== undefined &&
      deliveryStartHourValues.value !== null
    ) {
      if (
        (deliveryStartHourValues.value === now.getHours() && now.getMinutes() < 30) ||
        (deliveryStartHourValues.value === Number(deliveryStartTimeAry[0]) &&
          deliveryStartTimeAry[1] === "30") ||
        (editDeliveryRequest.value!.required_delivery_date === befor_data.delivery_date &&
          editDeliveryRequest.value!.delivery_id === befor_data.delivery_id &&
          deliveryStartHourValues.value ===
            Number(befor_data.irregularDeliveryStartTime[0]) &&
          befor_data.irregularDeliveryStartTime[1] === "30")
      ) {
        deliveryStartMinitItem.value.shift();
        deliveryStartMinitValues.value = { label: "30", deliveryStartMinit: 1 };
      } else if (
        deliveryStartHourValues.value === Number(endTimeAry[0]) &&
        endTimeAry[1] === "00"
      ) {
        deliveryStartMinitItem.value.pop();
        deliveryStartMinitValues.value = { label: "00", deliveryStartMinit: 0 };
      }
    }

    // 配達開始時間セット
    await setDeliveryStartTime();

    // 配達終了時間セット
    await setDeliveryEndTime();
  };

  /**
   * 配達希望時間（分）変更イベント @change
   */
  const onChangeDeliveryStartMinit = async (): Promise<void> => {
    if (
      deliveryStartMinitValues.value !== undefined &&
      deliveryStartMinitValues.value !== null
    ) {
      // 過去日時チェック
      if (!(await chkDeliveryStartTime_Now())) {
        deliveryStartMinitValues.value = undefined;
        errdialog.value = true;
        errMessage.value = "配達希望時間は、現在以降の時間を入力してください。";
        return;
      }
      // 配達開始時間前チェック
      if (!(await chkDeliveryStartTime())) {
        deliveryStartMinitValues.value = undefined;
        errdialog.value = true;
        errMessage.value =
          "配達希望時間は、選択した便の配達開始時間以降の時間を入力してください。";
        return;
      }
      // 受付終了時間超過チェック
      if (!(await chkDeliveryEndTimeOver())) {
        deliveryStartMinitValues.value = undefined;
        errdialog.value = true;
        errMessage.value =
          "配達希望時間は、受付終了時間以前の時間を入力してください。";
        return;
      }
    }

    // 配達開始時間セット
    await setDeliveryStartTime();

    // 配達終了時間セット
    await setDeliveryEndTime();
  };

  /**
   * 便選択 @click
   */
  const onDeliveryAvailabilityClick = async (
    date: string,
    deliveryid: string,
    delivery_start_time: string,
    delivery_end_time: string,
    deliverySelectDay: string,
    deliverySelect: string
  ): Promise<void> => {
    editDeliveryRequest.value!.delivery_id = Number(deliveryid);
    editDeliveryRequest.value!.required_delivery_date = date;

    let strDeliveryId = "";
    deliveryId_0_Flg.value = false;
    deliveryStartHourItem.value = [];
    deliveryStartHourValues.value = undefined;
    deliveryStartMinitValues.value = undefined;

    if (editDeliveryRequest.value!.delivery_id === 0) {
      // 配達開始時間セット
      let startHour = 0;
      const now = new Date();
      const nowDate =
        `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
      const deliveryStartTimeAry = delivery_start_time.split(":");

      if (
        date !== nowDate ||
        (date === nowDate &&
          (Number(deliveryStartTimeAry[0]) > now.getHours() ||
            (Number(deliveryStartTimeAry[0]) === now.getHours() &&
              Number(deliveryStartTimeAry[1]) > now.getMinutes())))
      ) {
        startHour = Number(deliveryStartTimeAry[0]);
      } else {
        startHour = now.getMinutes() < 30 ? now.getHours() : now.getHours() + 1;
      }

      const endTimeAry = acceptableEndTime.value.split(":");
      for (let i = startHour; i <= Number(endTimeAry[0]); i++) {
        deliveryStartHourItem.value.push(i);
      }

      deliveryStartMinitItem.value = [
        { label: "00", deliveryStartMinit: 0 },
        { label: "30", deliveryStartMinit: 1 },
      ];

      // 変更前の配達便と同一の場合、変更前の時間をセット
      if (
        date === befor_data.delivery_date &&
        Number(deliveryid) === befor_data.delivery_id &&
        Number(befor_data.irregularDeliveryStartTime[0]) < startHour
      ) {
        deliveryStartHourItem.value.unshift(
          Number(befor_data.irregularDeliveryStartTime[0])
        );
        deliveryStartHourValues.value = Number(
          befor_data.irregularDeliveryStartTime[0]
        );
        if (befor_data.irregularDeliveryStartTime[1] === "00") {
          deliveryStartMinitItem.value.pop();
          deliveryStartMinitValues.value = { label: "00", deliveryStartMinit: 0 };
        } else {
          deliveryStartMinitItem.value.shift();
          deliveryStartMinitValues.value = { label: "30", deliveryStartMinit: 1 };
        }
      }

      strDeliveryId = "都度便";
      deliveryId_0_Flg.value = true;

      deliveryStartTime.value = delivery_start_time;
      deliveryEndTime.value = delivery_end_time;

      // 配達開始時間セット
      await setDeliveryStartTime();

      // 配達終了時間セット
      await setDeliveryEndTime();

      editDeliveryRequest.value!.irregular_delivery_start_time = null;
      editDeliveryRequest.value!.irregular_delivery_end_time = null;
    } else {
      strDeliveryId = editDeliveryRequest.value!.delivery_id + "便";
    }

    // 画面表示用
    display_delivery_time.value =
      editDeliveryRequest.value!.required_delivery_date + " " + strDeliveryId;
    deliverySelectDayBox.value = deliverySelectDay;
    deliverySelectBox.value = deliverySelect;
  };

  /**
   * 曜日変換
   */
  const formatDate = (date: string): string => {
    let _date;
    let retDate = "";
    if (date) {
      _date = new Date(date);
      retDate =
        (_date.getMonth() + 1) +
        "/" +
        _date.getDate() +
        "(" +
        WEEKS[_date.getDay()] +
        ")";
    }
    return retDate;
  };

  return {
    onClickListDeliveryArea,
    getAreaList,
    getAreaInfo,
    getDeliveryTimeList,
    chkDeliveryStartTime_Now,
    chkDeliveryStartTime,
    chkDeliveryEndTimeOver,
    setDeliveryStartTime,
    setDeliveryEndTime,
    onChangeDeliveryStartHour,
    onChangeDeliveryStartMinit,
    onDeliveryAvailabilityClick,
    formatDate,
  };
};
